import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';

/**
 * Form primitives — Input, Textarea, Select, Label, FormField, HelpText.
 * Consistent ring, sizing, error state across all of them.
 */

const FIELD_BASE = clsx(
    'block w-full bg-surface text-ink',
    'border border-line rounded-lg',
    'placeholder:text-ink-faint',
    'transition-[border-color,box-shadow,background-color] duration-200 ease-smooth',
    'hover:border-line-strong',
    'focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/25',
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-surface-muted',
);

const FIELD_SIZES = {
    sm: 'text-sm px-3 py-2',
    md: 'text-base px-4 py-2.5',
    lg: 'text-lg px-5 py-3',
};

const ERROR_CLASSES = 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20 bg-danger-50/40';

export const Input = forwardRef(function Input({
    type = 'text',
    size = 'md',
    error = false,
    fullWidth = true,
    icon,
    iconPosition = 'start',
    className,
    ...rest
}, ref) {
    const sizeClasses = FIELD_SIZES[size] || FIELD_SIZES.md;
    const stateClasses = error ? ERROR_CLASSES : '';
    const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';

    if (icon) {
        return (
            <div className={clsx('relative', fullWidth ? 'w-full' : 'inline-block')}>
                <span
                    aria-hidden="true"
                    className={clsx(
                        'pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink-subtle inline-flex items-center justify-center',
                        iconSize,
                        iconPosition === 'start' ? 'start-3' : 'end-3',
                    )}
                >
                    {icon}
                </span>
                <input
                    ref={ref}
                    type={type}
                    className={clsx(
                        FIELD_BASE,
                        sizeClasses,
                        stateClasses,
                        iconPosition === 'start' ? 'ps-10' : 'pe-10',
                        className,
                    )}
                    {...rest}
                />
            </div>
        );
    }

    return (
        <input
            ref={ref}
            type={type}
            className={clsx(FIELD_BASE, sizeClasses, stateClasses, !fullWidth && 'w-auto', className)}
            {...rest}
        />
    );
});

export const Textarea = forwardRef(function Textarea({
    rows = 4,
    error = false,
    className,
    ...rest
}, ref) {
    return (
        <textarea
            ref={ref}
            rows={rows}
            className={clsx(
                FIELD_BASE,
                'text-base px-4 py-2.5 resize-y',
                error && ERROR_CLASSES,
                className,
            )}
            {...rest}
        />
    );
});

export const Select = forwardRef(function Select({
    size = 'md',
    error = false,
    className,
    children,
    ...rest
}, ref) {
    const sizeClasses = FIELD_SIZES[size] || FIELD_SIZES.md;
    return (
        <div className="relative">
            <select
                ref={ref}
                className={clsx(
                    FIELD_BASE,
                    sizeClasses,
                    'appearance-none pe-10 cursor-pointer',
                    error && ERROR_CLASSES,
                    className,
                )}
                {...rest}
            >
                {children}
            </select>
            <svg
                aria-hidden="true"
                className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle"
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        </div>
    );
});

export function Label({ children, htmlFor, required = false, className }) {
    return (
        <label
            htmlFor={htmlFor}
            className={clsx('block text-sm font-semibold text-ink mb-1.5', className)}
        >
            {children}
            {required && <span className="text-danger-500 ms-1" aria-hidden="true">*</span>}
        </label>
    );
}

export function HelpText({ children, error = false, className }) {
    return (
        <p className={clsx(
            'text-xs mt-1.5',
            error ? 'text-danger-600' : 'text-ink-subtle',
            className,
        )}>
            {children}
        </p>
    );
}

/**
 * FormField — composes Label + control + HelpText with a stable id wiring.
 *
 * <FormField label="Email" required helpText="We never share">
 *   <Input type="email" />
 *   // or: {({ id }) => <Input id={id} ... />}
 * </FormField>
 */
export function FormField({
    label,
    required = false,
    error,
    helpText,
    children,
    className,
}) {
    const reactId = useId();
    const id = `field-${reactId}`;
    const hasError = !!error;
    const message = hasError ? error : helpText;

    const child = typeof children === 'function'
        ? children({ id, error: hasError })
        : React.isValidElement(children)
            ? React.cloneElement(children, {
                id: children.props.id || id,
                error: hasError || children.props.error,
                'aria-invalid': hasError || undefined,
                'aria-describedby': message ? `${id}-msg` : undefined,
            })
            : children;

    return (
        <div className={clsx('w-full', className)}>
            {label && <Label htmlFor={id} required={required}>{label}</Label>}
            {child}
            {message && <HelpText id={`${id}-msg`} error={hasError}>{message}</HelpText>}
        </div>
    );
}

export default Input;
